import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Camera, Tag, ExternalLink } from "lucide-react";

const TAGS = [
  { value: "before", label: "Before", color: "bg-orange-100 text-orange-700" },
  { value: "after", label: "After", color: "bg-green-100 text-green-700" },
  { value: "progress", label: "Progress", color: "bg-blue-100 text-blue-700" },
];

export default function PhotoGalleryPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState({ before: [], after: [], progress: [], untagged: [] });
  const [loading, setLoading] = useState(true);
  const [viewTab, setViewTab] = useState("all");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGallery(); }, [projectId]);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/photos/gallery/${projectId}`);
      setGallery(res.data);
    } catch (err) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleTag = async (photoId, tag) => {
    try {
      await axios.put(`${API}/photos/${photoId}/tag`, { tag });
      toast.success(`Tagged as ${tag}`);
      fetchGallery();
    } catch (err) {
      toast.error("Failed to tag photo");
    }
  };

  const allPhotos = [...gallery.before, ...gallery.after, ...gallery.progress, ...gallery.untagged];
  const displayed = viewTab === "all" ? allPhotos
    : viewTab === "compare" ? null
    : gallery[viewTab] || [];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="photo-gallery-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Camera className="w-6 h-6 text-indigo-600" /> Photo Gallery</h1>
          <p className="text-slate-600 mt-1">{gallery.total || allPhotos.length} photos | {gallery.before.length} before | {gallery.after.length} after</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {["all", "before", "progress", "after", "compare"].map(tab => (
          <Button key={tab} variant={viewTab === tab ? "secondary" : "ghost"} size="sm" onClick={() => setViewTab(tab)} className="capitalize" data-testid={`gallery-tab-${tab}`}>
            {tab === "compare" ? "Before/After" : tab}
          </Button>
        ))}
      </div>

      {/* Compare View */}
      {viewTab === "compare" && (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-orange-600 mb-3 flex items-center gap-1"><Tag className="w-4 h-4" /> Before ({gallery.before.length})</h3>
            <div className="space-y-3">
              {gallery.before.length > 0 ? gallery.before.map(p => (
                <PhotoCard key={p.photo_id} photo={p} />
              )) : <p className="text-sm text-slate-400">No "before" photos tagged</p>}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-green-600 mb-3 flex items-center gap-1"><Tag className="w-4 h-4" /> After ({gallery.after.length})</h3>
            <div className="space-y-3">
              {gallery.after.length > 0 ? gallery.after.map(p => (
                <PhotoCard key={p.photo_id} photo={p} />
              )) : <p className="text-sm text-slate-400">No "after" photos tagged</p>}
            </div>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewTab !== "compare" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayed.length > 0 ? displayed.map(photo => (
            <div key={photo.photo_id} className="relative group" data-testid={`photo-${photo.photo_id}`}>
              <a href={photo.url} target="_blank" rel="noopener noreferrer">
                <img src={photo.url} alt={photo.caption || "Photo"} className="w-full h-40 object-cover rounded-lg border" onError={(e) => { e.target.style.display = 'none'; }} />
              </a>
              {photo.tag && (
                <Badge className={`absolute top-2 left-2 text-xs ${TAGS.find(t => t.value === photo.tag)?.color || ""}`}>
                  {photo.tag}
                </Badge>
              )}
              {photo.caption && <p className="text-xs text-slate-500 mt-1 truncate">{photo.caption}</p>}
              {/* Tag buttons on hover */}
              <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                {TAGS.map(t => (
                  <button key={t.value} className={`text-[10px] px-1.5 py-0.5 rounded ${t.color} ${photo.tag === t.value ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                    onClick={(e) => { e.preventDefault(); handleTag(photo.photo_id, t.value); }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )) : (
            <div className="col-span-full text-center py-12"><Camera className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No photos in this category</p></div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoCard({ photo }) {
  return (
    <a href={photo.url} target="_blank" rel="noopener noreferrer" className="block relative group">
      <img src={photo.url} alt={photo.caption || "Photo"} className="w-full h-48 object-cover rounded-lg border" onError={(e) => { e.target.style.display = 'none'; }} />
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
        <ExternalLink className="w-6 h-6 text-white" />
      </div>
      {photo.caption && <p className="text-xs text-slate-500 mt-1">{photo.caption}</p>}
    </a>
  );
}
